import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { pageMeta } from "@/lib/seo";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { useServerFn } from "@/lib/server-fn-shim";
import { redeemInviteCode } from "@/lib/invites.functions";
import {
  isBiometricAvailable,
  isBiometricUnlockEnabled,
  wasFaceIdAsked,
  markUnlocked,
  isNative,
} from "@/lib/native";
import roavaWordmark from "@/assets/bundled/roava-website-logo-transparent.png";
import { InviteCodeHelp } from "@/components/InviteCodeHelp";
import { hydrateCachedTrip, type CachedTrip } from "@/lib/offline/trip-cache";
import { isOfflineNow } from "@/lib/offline/offline-auth";
import { consumeAuthReturnTo } from "@/lib/auth/resume-sync";
import { setPendingCode, clearPendingCode } from "@/lib/pending-invite";
import { ArrowLeft } from "lucide-react";

const roavaWebsiteLogo = roavaWordmark;

export const Route = createFileRoute("/login")({
  head: () =>
    pageMeta({
      title: "Sign in — Roava by WGP Apps",
      description:
        "Enter your invite code to join the trip on Roava, or sign in to your existing account.",
      path: "/login",
    }),
  component: Login,
});

// Code is normalized (whitespace stripped, uppercased) inside the component.

type Step = "code" | "signup" | "signin";

function Login() {
  const { t } = useTranslation();
  const nav = useNavigate();
  const redeemFn = useServerFn(redeemInviteCode);

  const [step, setStep] = useState<Step>("code");
  const [code, setCode] = useState("");
  const [codeError, setCodeError] = useState<string | null>(null);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [signInError, setSignInError] = useState<string | null>(null);

  const [busy, setBusy] = useState(false);
  const [offline, setOffline] = useState<boolean>(isOfflineNow());
  const [cachedTrip, setCachedTrip] = useState<CachedTrip | null>(null);
  const [dismissedOfflinePanel, setDismissedOfflinePanel] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      if (data.session) {
        const returnTo = consumeAuthReturnTo();
        nav({ to: (returnTo ?? "/trip") as "/trip" });
      }
    });
  }, [nav]);

  useEffect(() => {
    void hydrateCachedTrip().then((t) => setCachedTrip(t));
    const onOnline = () => setOffline(false);
    const onOffline = () => setOffline(true);
    if (typeof window !== "undefined") {
      window.addEventListener("online", onOnline);
      window.addEventListener("offline", onOffline);
    }
    return () => {
      if (typeof window !== "undefined") {
        window.removeEventListener("online", onOnline);
        window.removeEventListener("offline", onOffline);
      }
    };
  }, []);

  async function handleRetryConnection() {
    try {
      await fetch("/version.json", { cache: "no-store" });
      setOffline(false);
    } catch {
      setOffline(isOfflineNow());
      toast.error(t("login.still_offline"));
    }
  }

  async function postAuthNavigate() {
    // Honor an explicit returnTo (e.g. user came from /offline-control to
    // recover from a needs-login outbox state). Validated in consumeAuthReturnTo.
    const returnTo = consumeAuthReturnTo();
    if (isNative()) {
      try {
        const [available, alreadyEnabled, alreadyAsked] = await Promise.all([
          isBiometricAvailable(),
          isBiometricUnlockEnabled(),
          wasFaceIdAsked(),
        ]);
        if (available && !alreadyEnabled && !alreadyAsked) {
          markUnlocked();
          nav({ to: "/face-id-intro" });
          return;
        }
      } catch { /* noop */ }
    }
    markUnlocked();
    if (returnTo) {
      nav({ to: returnTo as "/trip" });
      return;
    }
    nav({ to: "/trip" });
  }

  function normalizeCode(raw: string): string {
    return raw.replace(/\s+/g, "").toUpperCase();
  }

  async function handleValidateCode(e: React.FormEvent) {
    e.preventDefault();
    setCodeError(null);
    const normalized = normalizeCode(code);
    setBusy(true);
    try {
      const { data, error } = await supabase.rpc("inspect_invite_code", {
        _code: normalized,
      });
      if (error) throw error;
      const status = Array.isArray(data) ? data[0]?.status : (data as any)?.status;
      switch (status) {
        case "ok":
          setCode(normalized);
          setStep("signup");
          return;
        case "malformed":
          setCodeError(t("login.err_code_malformed"));
          return;
        case "not_found":
          setCodeError(t("login.err_code_not_found"));
          return;
        case "revoked":
          setCodeError(t("login.err_code_revoked"));
          return;
        case "already_used":
          setCodeError(t("login.err_code_used"));
          return;
        default:
          setCodeError(t("login.err_code_generic"));
          return;
      }
    } catch (err: any) {
      console.error("[login] inspect_invite_code", err);
      setCodeError(t("login.err_code_generic"));
    } finally {
      setBusy(false);
    }
  }

  async function handleSignup(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const { data: signUpData, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/trip`,
          data: { full_name: name },
        },
      });
      if (error) throw error;

      if (signUpData.session) {
        // Have a session — redeem now.
        try {
          await redeemFn({ data: { code } });
          clearPendingCode();
        } catch (err: any) {
          // Stash for retry on next SIGNED_IN; don't sign the user out.
          setPendingCode(code);
          console.error("[signup] redeem failed, queued for retry", err);
        }
        toast.success(t("login.welcome_in"));
        await postAuthNavigate();
      } else {
        // Email confirmation required. Queue the code; AuthSync will redeem
        // on the first SIGNED_IN event.
        setPendingCode(code);
        toast.success(t("login.check_email"));
        setStep("signin");
      }
    } catch (err: any) {
      console.error("[signup]", err?.code, err?.status, err?.message);
      const msg = err?.message ?? "";
      const ecode = err?.code ?? "";
      if (
        /already registered|already exists|user_already_exists/i.test(msg) ||
        ecode === "user_already_exists"
      ) {
        toast.error(t("login.err_email_taken"));
      } else if (ecode === "weak_password" || /pwned|data breach|leaked|compromised/i.test(msg)) {
        toast.error(t("login.err_password_pwned"));
      } else if (/password should be at least|password.*weak|password.*short/i.test(msg)) {
        toast.error(t("login.err_password_weak"));
      } else if (
        /invalid.*email|email.*invalid|email_address_invalid/i.test(msg) ||
        ecode === "email_address_invalid"
      ) {
        toast.error(t("login.err_email_invalid"));
      } else if (/rate limit|over_email_send_rate_limit|too many/i.test(msg)) {
        toast.error(t("login.err_rate_limit"));
      } else {
        toast.error(
          msg ? t("login.err_signup_with_msg", { msg: msg.slice(0, 140) }) : t("login.err_signup_generic"),
        );
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleSignin(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setSignInError(null);
    try {
      const { error } = await supabase.auth.signInWithPassword({ email, password });
      if (error) throw error;
      await postAuthNavigate();
    } catch (err: any) {
      console.error("[signin]", err?.code, err?.status, err?.message);
      const msg = err?.message ?? "";
      const code = err?.code ?? "";

      if (/invalid login credentials/i.test(msg)) {
        setSignInError(t("login.err_wrong_password"));
      } else if (/email not confirmed|confirm/i.test(msg)) {
        setSignInError(t("login.err_confirm_email"));
      } else if (/rate limit|too many|over_email_send_rate_limit/i.test(msg)) {
        setSignInError(t("login.err_rate_limit"));
      } else if (/failed to fetch|network|timeout|load failed/i.test(msg)) {
        setSignInError(t("login.err_network"));
      } else if (code === "session_not_found" || code === "no_session" || /auth session missing/i.test(msg)) {
        setSignInError(t("login.err_session"));
      } else {
        setSignInError(msg ? t("login.err_signin_with_msg", { msg: msg.slice(0, 140) }) : t("login.err_signin_generic"));
      }
    } finally {
      setBusy(false);
    }
  }

  const stepTitle =
    step === "code"
      ? t("login.title_code")
      : step === "signup"
        ? t("login.title_signup")
        : t("login.title_signin");
  const stepSubtitle =
    step === "code"
      ? t("login.subtitle_code")
      : step === "signup"
        ? t("login.subtitle_signup")
        : t("login.subtitle_signin");

  return (
    <div className="min-h-dvh bg-background">
      <div
        className="mx-auto max-w-[480px] px-5"
        style={{
          paddingTop: "calc(env(safe-area-inset-top) + 0.25rem)",
          paddingBottom: "calc(env(safe-area-inset-bottom) + 1.5rem)",
        }}
      >
        {step === "code" ? (
          <Link to="/" className="text-sm text-muted-foreground">
            ← {t("login.back")}
          </Link>
        ) : (
          <button
            type="button"
            onClick={() => setStep("code")}
            className="inline-flex items-center gap-1 text-sm text-muted-foreground"
          >
            <ArrowLeft className="size-4" /> {t("login.back")}
          </button>
        )}

        <div className="mt-6 flex items-center">
          <img
            src={roavaWebsiteLogo}
            alt="Roava by WGP Apps"
            className="h-auto w-full max-w-[220px]"
          />
        </div>

        {offline && !dismissedOfflinePanel && (
          <div className="mt-6 rounded-2xl border border-border bg-card p-5">
            <h2 className="text-xl font-semibold">{t("login.offline_title")}</h2>
            <p className="mt-2 text-sm text-muted-foreground">
              {t("login.offline_body")}
            </p>
            <div className="mt-4 grid gap-2">
              {cachedTrip && (
                <Button
                  type="button"
                  onClick={() => nav({ to: "/offline-control" })}
                  className="h-12 rounded-xl"
                >
                  {t("login.offline_open_controls")}
                </Button>
              )}
              <Button
                type="button"
                variant="secondary"
                onClick={handleRetryConnection}
                className="h-12 rounded-xl"
              >
                {t("login.retry_connection")}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setDismissedOfflinePanel(true)}
                className="h-12 rounded-xl"
              >
                {t("login.sign_in_when_online")}
              </Button>
            </div>
          </div>
        )}

        <h1 className="mt-5 text-3xl font-bold">{stepTitle}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{stepSubtitle}</p>

        {step === "code" && (
          <>
            <form onSubmit={handleValidateCode} className="mt-6 grid gap-3">
              <div className="grid gap-2">
                <Label htmlFor="code">{t("login.invite_code")}</Label>
                <Input
                  id="code"
                  value={code}
                  onChange={(e) => {
                    setCode(normalizeCode(e.target.value));
                    if (codeError) setCodeError(null);
                  }}
                  placeholder={t("login.code_placeholder")}
                  autoCapitalize="characters"
                  autoComplete="off"
                  required
                  className="h-14 rounded-xl text-center text-lg font-mono tracking-[0.2em] uppercase"
                />
                {codeError && (
                  <p className="text-sm text-destructive" role="alert">
                    {codeError}
                  </p>
                )}
                <InviteCodeHelp />
              </div>
              <Button
                type="submit"
                disabled={busy || code.trim().length < 6}
                className="h-14 mt-2 rounded-2xl text-base font-semibold"
              >
                {busy ? t("login.checking") : t("login.continue")}
              </Button>
            </form>

            <button
              type="button"
              onClick={() => setStep("signin")}
              className="mt-6 w-full text-center text-sm text-muted-foreground"
            >
              {t("login.have_account")}
            </button>
          </>
        )}

        {step === "signup" && (
          <form onSubmit={handleSignup} className="mt-6 grid gap-3">
            <div className="rounded-xl border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
              {t("login.code_accepted", { code })}
            </div>
            <div className="grid gap-2">
              <Label htmlFor="name">{t("login.your_name")}</Label>
              <Input
                id="name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="h-12 rounded-xl"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="email">{t("login.email")}</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-12 rounded-xl"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="password">{t("login.password")}</Label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={8}
                className="h-12 rounded-xl"
              />
            </div>
            <Button
              type="submit"
              disabled={busy}
              className="h-14 mt-2 rounded-2xl text-base font-semibold"
            >
              {busy ? t("login.please_wait") : t("login.create_account")}
            </Button>
          </form>
        )}

        {step === "signin" && (
          <>
            <form onSubmit={handleSignin} className="mt-6 grid gap-3">
              <div className="grid gap-2">
                <Label htmlFor="email">{t("login.email")}</Label>
                <Input
                  id="email"
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (signInError) setSignInError(null);
                  }}
                  required
                  className="h-12 rounded-xl"
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="password">{t("login.password")}</Label>
                <Input
                  id="password"
                  type="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (signInError) setSignInError(null);
                  }}
                  required
                  minLength={8}
                  className="h-12 rounded-xl"
                />
              </div>
              <Button
                type="submit"
                disabled={busy}
                className="h-14 mt-2 rounded-2xl text-base font-semibold"
              >
                {busy ? t("login.please_wait") : t("login.sign_in")}
              </Button>
              {signInError && (
                <div
                  className="rounded-xl border border-destructive/20 bg-destructive/5 px-3 py-2 text-sm text-destructive"
                  role="alert"
                >
                  {signInError}
                </div>
              )}
            </form>

            <button
              type="button"
              onClick={() => {
                setSignInError(null);
                setStep("code");
              }}
              className="mt-6 w-full text-center text-sm text-muted-foreground"
            >
              {t("login.new_user")}
            </button>
          </>
        )}

        <p className="mt-6 text-center text-[11px] text-muted-foreground/70">
          {t("login.built_by")}{" "}
          <a
            href="https://wgpapp.com"
            target="_blank"
            rel="noopener noreferrer"
            className="underline-offset-2 hover:underline"
          >
            WGP Apps
          </a>
        </p>
      </div>
    </div>
  );
}
